import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Checkbox,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@/components/ui";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import {
  getSelectablePageIds,
  PermissionPageAccordion,
} from "@/components/features/settings/permission-groups/PermissionPageAccordion";
import {
  type PermissionCatalogDefinition,
  type PermissionGrant,
  type PermissionGroupAction,
} from "@/lib/types";
import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

const actionLabel = (action: PermissionGroupAction) =>
  action === "notify.low"
    ? "Notify (Low Priority)"
    : action === "notify.high"
      ? "Notify (High Priority)"
      : action.replace(/\b\w/g, (letter) => letter.toUpperCase());

export const isNotificationAction = (action: PermissionGroupAction) =>
  action.startsWith("notify.");

export function NotificationCatalogBulkActions({
  catalog,
  onActionsChange,
}: {
  catalog: PermissionCatalogDefinition[];
  onActionsChange: (
    permissionId: string,
    actions: PermissionGroupAction[],
    isNotification: boolean,
  ) => void;
}) {
  const [priority, setPriority] = useState<PermissionGroupAction>("notify.low");
  const priorityLabel =
    priority === "notify.high" ? "High priority" : "Low priority";

  return (
    <div className="flex justify-end gap-2">
      <div className="flex">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="rounded-r-none border-r-0"
          onClick={() => {
            catalog.forEach((permission) => {
              onActionsChange(
                permission.id,
                [
                  permission.actions.find(({ action }) => action === priority)
                    ?.action ?? permission.actions[0].action,
                ],
                true,
              );
            });
          }}
        >
          Select all ({priorityLabel})
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-l-none px-2"
              title="Notification priority"
            >
              <ChevronDown className="h-4 w-4" />
              <span className="sr-only">Notification priority</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {["notify.low", "notify.high"].map((nextPriority) => (
              <DropdownMenuItem
                key={nextPriority}
                onClick={() => {
                  setPriority(nextPriority as PermissionGroupAction);
                }}
              >
                {nextPriority === "notify.low"
                  ? "Low priority"
                  : "High priority"}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => {
          catalog.forEach((permission) => {
            onActionsChange(permission.id, [], true);
          });
        }}
      >
        Clear all
      </Button>
    </div>
  );
}

export function PermissionCatalogGrants({
  catalog,
  grants,
  onActionsChange,
}: {
  catalog: PermissionCatalogDefinition[];
  grants: PermissionGrant[];
  onActionsChange: (
    permissionId: string,
    actions: PermissionGroupAction[],
    isNotification: boolean,
  ) => void;
}) {
  if (!catalog.length)
    return <Text variant="subHeading">No options are available.</Text>;

  return catalog.map((permission) => (
    <PermissionActionSelect
      key={permission.id}
      permission={permission}
      selectedActions={grants
        .filter(
          (grant) =>
            grant.permissionId === permission.id &&
            permission.actions.some(({ action }) => action === grant.action),
        )
        .map((grant) => grant.action)}
      onActionsChange={onActionsChange}
    />
  ));
}

function PermissionActionSelect({
  permission,
  selectedActions,
  onActionsChange,
}: {
  permission: PermissionCatalogDefinition;
  selectedActions: PermissionGroupAction[];
  onActionsChange: (
    permissionId: string,
    actions: PermissionGroupAction[],
    isNotification: boolean,
  ) => void;
}) {
  const [values, setValues] = useState<string[]>(selectedActions);
  useEffect(() => {
    setValues(selectedActions);
  }, [selectedActions]);
  const isNotification = permission.actions.some(({ action }) =>
    isNotificationAction(action),
  );
  const singlePageAction =
    permission.kind === "page" &&
    !isNotification &&
    permission.actions.length === 1
      ? permission.actions[0].action
      : undefined;

  return (
    <div className="hover:bg-muted/50 flex items-center justify-between gap-4 rounded-md px-2 py-2 transition-colors">
      <div className="min-w-0">
        <Text variant="body">{permission.label}</Text>
      </div>
      {singlePageAction ? (
        <label className="flex items-center gap-2">
          <Checkbox
            checked={selectedActions.includes(singlePageAction)}
            onCheckedChange={(checked) => {
              onActionsChange(
                permission.id,
                checked ? [singlePageAction] : [],
                false,
              );
            }}
          />
          <Text variant="subHeading">{actionLabel(singlePageAction)}</Text>
        </label>
      ) : isNotification ? (
        <Select
          value={selectedActions.at(0) ?? "none"}
          onValueChange={(value) => {
            onActionsChange(
              permission.id,
              value === "none" ? [] : [value as PermissionGroupAction],
              true,
            );
          }}
        >
          <SelectTrigger className="w-72">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {permission.actions.map(({ action }) => (
              <SelectItem key={action} value={action}>
                {actionLabel(action)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <MultiSelect
          options={permission.actions.map(({ action }) => ({
            label: actionLabel(action),
            value: action,
          }))}
          selectedValues={values}
          setSelectedValues={setValues}
          onValueChange={(next) => {
            onActionsChange(
              permission.id,
              next as PermissionGroupAction[],
              false,
            );
          }}
          placeholder="No actions"
          showToggleAll={false}
          showChips
          maxCount={Number.POSITIVE_INFINITY}
          modalPopover
          className="w-72"
        />
      )}
    </div>
  );
}

export function PermissionCatalogSection({
  value,
  title,
  catalog,
  grants,
  onActionsChange,
}: {
  value: string;
  title: string;
  catalog: PermissionCatalogDefinition[];
  grants: PermissionGrant[];
  onActionsChange: (
    permissionId: string,
    actions: PermissionGroupAction[],
    isNotification: boolean,
  ) => void;
}) {
  if (!catalog.length) return null;

  const isPageCatalog = catalog.every(
    (permission) => permission.kind === "page",
  );
  const selectablePageIds = getSelectablePageIds(catalog);
  const selectedPageCount = new Set(
    grants
      .filter(
        (grant) =>
          grant.action === "view" && selectablePageIds.has(grant.permissionId),
      )
      .map((grant) => grant.permissionId),
  ).size;
  const selectedActionCount = new Set(
    grants
      .filter((grant) =>
        catalog.some(
          (permission) =>
            permission.id === grant.permissionId &&
            permission.actions.some(({ action }) => action === grant.action),
        ),
      )
      .map((grant) => `${grant.permissionId}:${grant.action}`),
  ).size;
  const selectedCount = isPageCatalog ? selectedPageCount : selectedActionCount;
  const countLabel = isPageCatalog
    ? selectedCount === 1
      ? "page"
      : "pages"
    : selectedCount === 1
      ? "action"
      : "actions";

  const allSelected = catalog.every((permission) =>
    permission.actions.every(({ action }) =>
      grants.some(
        (grant) =>
          grant.permissionId === permission.id && grant.action === action,
      ),
    ),
  );

  const toggleAll = () => {
    if (allSelected) {
      const permissions = isPageCatalog ? [...catalog].reverse() : catalog;
      permissions.forEach((permission) => {
        onActionsChange(permission.id, [], false);
      });
      return;
    }

    catalog.forEach((permission) => {
      onActionsChange(
        permission.id,
        permission.actions.map(({ action }) => action),
        false,
      );
    });
  };

  return (
    <AccordionItem value={value}>
      <div className="flex items-center gap-2">
        <AccordionTrigger iconPosition="left" className="justify-start gap-2">
          <span>{title}</span>
          <Badge
            variant="secondary"
            className="tabular-nums"
            aria-label={`${selectedCount} selected ${countLabel}`}
          >
            {selectedCount} {countLabel}
          </Badge>
        </AccordionTrigger>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="shrink-0"
          onClick={toggleAll}
        >
          {allSelected ? "Clear all" : "Select all"}
        </Button>
      </div>
      <AccordionContent className="space-y-3 pt-2">
        {isPageCatalog ? (
          <PermissionPageAccordion
            catalog={catalog}
            grants={grants}
            onActionsChange={onActionsChange}
          />
        ) : (
          <PermissionCatalogGrants
            catalog={catalog}
            grants={grants}
            onActionsChange={onActionsChange}
          />
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
